import { makeAutoObservable, runInAction } from 'mobx'
import { injectable, inject, decorate } from 'inversify'
import { debounce, uniqBy } from 'lodash'

// evertel
import { Api } from '@evertel/api'
import { APIDataMedia } from '@evertel/types'
import { NormalizedMedia } from '../NormalizedMediaType'
import { APIDataMediaToNormalizedMedia } from '../APIDataMediaToNormalizedMedia'

class RoomMediaSearchController {
    media: NormalizedMedia[] = []
    mediaLatestFetch: NormalizedMedia[] = []
    roomId = 0
    searchTerm = ''
    mimeType = ''
    page = 1
    limit = 10
    mediaCount = 0
    isInfiniteScroll = true
    isLoading = false

    constructor(
        private api: Api
    ) {
        makeAutoObservable(this)
    }

    init(roomId: number, isInfiniteScroll = true) {
        this.roomId = roomId
        this.isInfiniteScroll = isInfiniteScroll
        this.resetState()
    }

    setSearchTerm(value: string) {
        this.resetSearchMeta()
        this.searchTerm = value
    }

    setMimetype(value: string) {
        this.resetSearchMeta()
        this.mimeType = value
    }

    setPage(page: number) {
        this.page = page
    }

    debouncedSearch = async () => {
        return debounce(async () => this.search(), 300)
    }

    search = async () => {
        if (!this.roomId) return

        runInAction(() => {
            this.isLoading = true
        })

        // need to grab these here because of async issues that can happen after the first await
        const localFilter = this.filter
        const localSearchTerm = this.searchTerm
        const localRoomId = this.roomId
        const localPage = this.page

        let count = this.mediaCount
        let results: APIDataMedia[] = []

        this.mediaLatestFetch = []

        try {

            // grab the total count of media in this room
            if (!this.mediaCount) {
                count = await this.fetchMediaCount(localFilter.where)
            }

            // stop from fetching if we already got them all
            if (count > this.media?.length) {
                results = await this.api.Routes.Room.getMedia(localRoomId, localFilter)
            }

            const normalizedResults = results.map(m => APIDataMediaToNormalizedMedia(m))
            const apiDataResults = (this.isInfiniteScroll) ? uniqBy([...this.media, ...normalizedResults], 'id') : normalizedResults

            // Check if parameters have changed before updating state
            if ( this.searchTerm !== localSearchTerm || this.roomId !== localRoomId ) {
                console.log(':::::: RoomMediaSearchController   stale, aborted:', localSearchTerm, this.searchTerm, localFilter, this.filter)
                // Parameters have changed; discard stale results
                return
            }

            runInAction(() => {
                this.mediaLatestFetch = normalizedResults
                this.media = apiDataResults
            })

        } finally {
            runInAction(() => {
                this.isLoading = false
            })
        }

        // console.log('::::: RoomMediaSearchController   params', `
        //     term: ${this.searchTerm} !== ${localSearchTerm}
        //     page: ${this.page} !== ${localPage}`)
        // console.log(':::::: RoomMediaSearchController   mediaLength:', this.media.length)
        // console.log(':::::: RoomMediaSearchController   resultsLength:', results.length)
        console.log(':::::: RoomMediaSearchController   filter:', localFilter)

    }

    fetchNextMedia() {
        this.page = this.page + 1
        this.search()
    }

    async fetchMediaCount(where = this.filter.where) {
        const res = await this.api.Routes.Room.getMediaCount(this.roomId, where)

        runInAction(() => {
            this.mediaCount = res?.count ?? 0
        })

        return this.mediaCount
    }

    resetSearchMeta() {
        this.page = 1
        this.media = []
        this.mediaCount = 0
    }

    resetState() {
        this.resetSearchMeta()
        this.searchTerm = ''
        this.mimeType = ''
    }

    get filter() {
        const f = {
            where: {},
            order: 'updatedDate DESC',
            limit: this.limit,
            skip: (this.page - 1) * this.limit
        }

        if (this.searchTerm !== '') {
            f.where['fileName'] = { like: `%${this.searchTerm}%` }
        }

        return f
    }

    get images() {
        return this.media?.filter(m => m.mediaType === 'image')
    }
}

decorate(injectable(), RoomMediaSearchController)
decorate(inject(Api), RoomMediaSearchController, 0)

export { RoomMediaSearchController }
